import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, Quote } from 'lucide-react';
import { scenarioData, tournamentCosts } from './scenarioData';

export const ExecutiveSummary = ({ selectedScenario }) => {
  const scenario = scenarioData[selectedScenario];
  
  // Calculate totals
  const totalValueCreation = 
    scenario.brandValueIncrease + 
    scenario.sponsorshipIncrease + 
    scenario.mediaRightsIncrease + 
    scenario.ticketRevenue + 
    scenario.merchandiseIncrease;
  
  const netROI = totalValueCreation - tournamentCosts.total;
  const roiMultiple = totalValueCreation > 0 ? (totalValueCreation / tournamentCosts.total).toFixed(1) : 0;
  
  // Generate dynamic summary based on scenario
  const getSummaryText = () => {
    const scenarioLabel = scenario.label;
    const netFormatted = netROI >= 0 ? `+$${netROI}M` : `-$${Math.abs(netROI)}M`;
    
    if (scenario.id === 'group_stage') {
      return `Group stage elimination as co-host would result in net value destruction of ${netFormatted}. This outcome materially damages long-term sponsorship negotiations and brand positioning for the 2027-2030 commercial cycle.`;
    }
    
    if (scenario.id === 'round_of_32') {
      return `Round of 32 advancement represents minimum knockout-stage participation in the 48-team format, delivering net value creation of ${netFormatted} (${roiMultiple}x return). As co-host, this is below the quarterfinal base-case expectation.`;
    }
    
    if (scenario.id === 'round_of_16') {
      return `Round of 16 advancement delivers modest net value creation of ${netFormatted} (${roiMultiple}x return). This baseline outcome meets minimum expectations but provides limited differentiation from 2022 performance.`;
    }
    
    if (scenario.id === 'quarterfinal') {
      return `Quarterfinal achievement represents base case with net value creation of ${netFormatted} (${roiMultiple}x return). This outcome positions favorably for 2027-2030 commercial cycle negotiations while meeting market expectations.`;
    }
    
    if (scenario.id === 'semifinal') {
      return `If USMNT reaches the Semifinals, estimated net value creation is ${netFormatted} (${roiMultiple}x cost). This materially strengthens long-term sponsorship leverage ahead of 2026 cycle peak.`;
    }
    
    return `World Cup championship would generate transformational net value creation of ${netFormatted} (${roiMultiple}x return). This outcome fundamentally reshapes USMNT brand positioning as a global Tier-1 property.`;
  };

  return (
    <section className="mb-10" data-testid="executive-summary-section">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="w-6 h-6 text-primary" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Executive Insight Summary</h2>
      </div>
      
      <Card className="bg-card border-border/50">
        <CardContent className="p-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="p-2 rounded-lg bg-primary/10">
                <Quote className="w-5 h-5 text-primary" />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-base text-foreground leading-relaxed mb-4" data-testid="summary-text">
                {getSummaryText()}
              </p>
              
              <div className="p-4 rounded-lg bg-muted/20 border border-border/30">
                <p className="text-sm text-muted-foreground italic">
                  {scenario.insight}
                </p>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-border/30">
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Scenario</span>
                  <p className="font-semibold text-foreground">{scenario.label}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Probability</span>
                  <p className="font-semibold text-foreground">{scenario.probability}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Net Impact</span>
                  <p className={`font-semibold ${netROI >= 0 ? 'text-stable' : 'text-destructive'}`}>
                    {netROI >= 0 ? '+' : ''}{netROI}M ({roiMultiple}x)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default ExecutiveSummary;
